<template>
    <div class="qd-login-v3-page">
        <div ref="staticRoot" class="qd-login-v3-root"></div>
        <div
            v-show="loginVisible"
            :id="loginContainerId"
            class="qd-login-v3-overlay"
        ></div>
    </div>
</template>

<script>
import {get as getCookie} from '@/lib/cookie';
import {APP_ID} from '@/common/config';
import {createPassportLogin} from './LoginV3Static/passport-login';

const LOGIN_CONTAINER_ID = 'login-v3-login-container';
const STATIC_BASE = '/static/loginV3-static';
const STATIC_STYLES = ['base-styles.min.css', 'styles.min.css'];
const STATIC_SCRIPTS = ['hero-particles.min.js', 'base-script.min.js', 'script.min.js'];
const LOGIN_TRIGGER_SELECTOR = '[data-action="login"]';
const LOGIN_SUBMIT_SELECTOR = '[data-action="login-submit"]';

function loadStyle(href) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.dataset.loginV3Static = 'true';
    document.head.appendChild(link);
    return link;
}

function loadScript(src) {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.dataset.loginV3Static = 'true';
        script.onload = () => resolve(script);
        script.onerror = reject;
        document.body.appendChild(script);
    });
}

export default {
    name: 'LoginV3',
    inject: ['$toast'],
    data() {
        return {
            loginVisible: false,
            loginReady: false,
            passportLogin: null,
            styleEls: [],
            scriptEls: [],
            bodyAttrNames: []
        };
    },
    computed: {
        loginContainerId() {
            return LOGIN_CONTAINER_ID;
        }
    },
    created() {
        const CPID = getCookie(`CPID_${APP_ID}`) || '';
        const CPTK = getCookie(`CPTK_${APP_ID}`) || '';
        this.$store.dispatch('getUserInfo', {
            allowNotLogin: true,
            userid: CPID,
            optid: CPID,
            token: CPTK
        }).then(data => {
            if (data?.accountInfo) {
                this.$router.push({path: `/user/${CPID}/index`});
            }
        }).catch(() => {});
    },
    async mounted() {
        this.loadStaticStyles();
        // [QD-CUSTOM] 项目全局主题（veui-theme-dls）通过 App.vue 引入了裸的 ::-webkit-scrollbar
        // 规则（无选择器前缀限定）。Blink/WebKit 只要文档 CSSOM 里存在过这条规则，就会把整个
        // 文档判定为"作者已接管滚动条外观"，从而放弃系统级 overlay 滚动条、改为占位的 classic
        // 滚动条——这是浏览器渲染引擎的既定机制，无法通过覆盖数值（包括 initial/极小值）恢复，
        // 只能把这条规则从 CSSOM 里整条删除。用 deleteRule 精确删除，不影响同一 <style>/<link>
        // 内的其他全局样式（字体、#app 布局等）。
        this.removeGlobalScrollbarRule();
        const injected = await this.injectStaticMarkup();
        if (!injected) {
            return;
        }
        // [QD-CUSTOM] 关闭浏览器默认的滚动位置恢复，确保刷新页面总是从首屏开始。
        // 原因：LoginV3 是整屏 scroll-snap 页面，浏览器默认 scrollRestoration:'auto'
        // 会在刷新时尝试还原上次的 scrollY，配合 scroll-snap 吸附到最近整屏，
        // 造成"刷新后仍停留在原来那屏"的现象（不符合预期，预期刷新即重置到首屏）。
        this.prevScrollRestoration = window.history.scrollRestoration;
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }
        window.scrollTo(0, 0);
        this.bindLoginTriggers();
        window.addEventListener('scroll', this.handleScroll, {passive: true});
        this.passportLogin = createPassportLogin({
            containerId: LOGIN_CONTAINER_ID,
            toast: this.$toast,
            onReady: () => {
                this.loginReady = true;
            }
        });
        setTimeout(() => {
            this.passportLogin.initLogin();
        }, 10);
        this.loadStaticScripts();
    },
    beforeDestroy() {
        this.unbindLoginTriggers();
        window.removeEventListener('scroll', this.handleScroll);
        // [QD-CUSTOM] 还原 scrollRestoration，避免影响站内其他页面的默认滚动恢复行为
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = this.prevScrollRestoration || 'auto';
        }
        this.styleEls.forEach(el => el.remove());
        this.scriptEls.forEach(el => el.remove());
        this.bodyAttrNames.forEach(name => document.body.removeAttribute(name));
    },
    methods: {
        // [QD-CUSTOM] 从 CSSOM 里精确删除 veui-theme-dls 注入的全局 ::-webkit-scrollbar
        // 规则（selectorText 精确匹配，只删这一条，不影响同一 stylesheet 内的其他全局样式）。
        // dev/生产构建下该规则所在的 <style>/<link> 不固定，因此遍历所有可访问的 styleSheets；
        // 跨域且无 CORS 头的表读取 cssRules 会抛错，直接跳过即可。
        removeGlobalScrollbarRule() {
            const targets = ['::-webkit-scrollbar', '::-webkit-scrollbar-thumb', ':hover::-webkit-scrollbar-thumb'];
            Array.from(document.styleSheets).forEach(sheet => {
                let rules;
                try {
                    rules = sheet.cssRules;
                }
                catch (e) {
                    return;
                }
                if (!rules) {
                    return;
                }
                for (let i = rules.length - 1; i >= 0; i--) {
                    if (targets.includes(rules[i].selectorText)) {
                        sheet.deleteRule(i);
                    }
                }
            });
        },
        loadStaticStyles() {
            this.styleEls = STATIC_STYLES.map(name => loadStyle(`${STATIC_BASE}/${name}`));
        },
        async injectStaticMarkup() {
            try {
                const res = await fetch(`${STATIC_BASE}/index.html`);
                const html = await res.text();
                const bodyMatch = html.match(/<body([^>]*)>([\s\S]*)<\/body>/i);
                const bodyAttrs = bodyMatch ? bodyMatch[1] : '';
                // [QD-CUSTOM] 首屏 hero 区块的隐藏样式必须和内容在同一次 innerHTML 写入里
                // 一起生效，不能依赖外部 CSS 文件（base-styles.min.css）加载完成。
                // 原因：loadStaticStyles() 是页面已运行后才用 JS 动态插入的 <link>，浏览器
                // 不会像"解析 HTML 时发现的 <link>"那样阻塞渲染，导致该 CSS 文件还没下载完时
                // 浏览器已经把刚插入的内容画出来一帧（此时 #hero.card-reveal{opacity:0} 还没
                // 生效），等 CSS 加载完才补上隐藏样式——这就是"标题先显示、消失变空白、再播放
                // 动画显示"的真正根因，与 base-script.min.js 是否加载完成无关。用内联样式可以
                // 保证隐藏态和内容同时进入 DOM，不存在中间可见帧；base-styles.min.css 里的
                // #hero.card-reveal.is-visible 规则触发后会覆盖此内联样式，动效不受影响。
                const bodyContent = (bodyMatch ? bodyMatch[2] : html)
                    .replace(
                        /(<section\b[^>]*\bid="hero"[^>]*)(>)/i,
                        '$1 style="opacity:0"$2'
                    );
                this.applyBodyAttrs(bodyAttrs);
                this.$refs.staticRoot.innerHTML = bodyContent;
                return true;
            }
            catch (e) {
                this.$toast?.warn?.('页面加载失败，请刷新重试');
                return false;
            }
        },
        applyBodyAttrs(attrString) {
            const attrPattern = /([\w-]+)="([^"]*)"/g;
            let match = attrPattern.exec(attrString);
            while (match) {
                document.body.setAttribute(match[1], match[2]);
                this.bodyAttrNames.push(match[1]);
                match = attrPattern.exec(attrString);
            }
        },
        async loadStaticScripts() {
            for (const name of STATIC_SCRIPTS) {
                const el = await loadScript(`${STATIC_BASE}/${name}`); // eslint-disable-line no-await-in-loop
                this.scriptEls.push(el);
            }
        },
        bindLoginTriggers() {
            this.$refs.staticRoot.querySelectorAll(LOGIN_TRIGGER_SELECTOR).forEach(el => {
                el.addEventListener('click', this.handleLoginClick);
            });
            this.$refs.staticRoot.querySelectorAll(LOGIN_SUBMIT_SELECTOR).forEach(el => {
                el.addEventListener('submit', this.handleLoginClick);
            });
        },
        unbindLoginTriggers() {
            if (!this.$refs.staticRoot) {
                return;
            }
            this.$refs.staticRoot.querySelectorAll(LOGIN_TRIGGER_SELECTOR).forEach(el => {
                el.removeEventListener('click', this.handleLoginClick);
            });
            this.$refs.staticRoot.querySelectorAll(LOGIN_SUBMIT_SELECTOR).forEach(el => {
                el.removeEventListener('submit', this.handleLoginClick);
            });
        },
        handleLoginClick(event) {
            event.preventDefault();
            // [QD-CUSTOM] SDK 是异步渲染的（initLogin 内部 setTimeout 延迟调用 + 弹窗 DOM
            // 渲染完成才触发 rendered），如果用户在渲染完成前点击登录按钮，loginVisible 置为
            // true 会让蒙层/模糊态生效，但此时 show() 没有实际弹窗内容可显示，蒙层会一直卡住、
            // 无法关闭。这里加就绪判断兜底，未就绪时提示用户稍候，不触发蒙层。
            if (!this.loginReady) {
                this.$toast?.warn?.('登录组件加载中，请稍候重试');
                return;
            }
            window.scrollTo({top: 0, behavior: 'auto'});
            this.loginVisible = true;
            this.toggleLoginBodyClass();
            this.passportLogin?.show();
        },
        handleScroll() {
            // if (this.loginVisible && window.scrollY > 0) {
            //     this.loginVisible = false;
            // }
        },
        // 加一个给body切换class的方法，根据loginVisible 来给body增加或者移除【is-login-show】
        toggleLoginBodyClass() {
            if (this.loginVisible) {
                document.body.classList.add('is-login-show');
            } else {
                document.body.classList.remove('is-login-show');
            }
        }
    },
    watch: {
        loginVisible(newValue) {
            this.toggleLoginBodyClass(newValue);
        }
    }
};
</script>

<style lang="less" scoped>
// [QD-CUSTOM][2026-09-09] 静态壳内容通过 fetch 异步注入，在 injectStaticMarkup 完成前
// staticRoot 容器是空的，此时会露出 <body> 默认背景，页面左上角短暂闪过一点内容
// （通常是 App.vue 全局背景/文字），效果不好。这里在页面根节点覆盖一层纯色背景铺满
// 视口，遮住这段"空白到有内容"之间的间隙，等静态内容注入后视觉上无缝过渡（静态包
// 首屏 body 背景同样是 #fff，颜色一致不会有二次跳变）。
.qd-login-v3-page {
    position: relative;
    min-height: 100vh;
    background: #fff;
}

.qd-login-v3-overlay {
    position: fixed;
    top: 50%;
    left: 50%;
    margin-left: 228px;
    z-index: 9;
    overflow: hidden;
    transform: translateY(-50%);
    padding-bottom: calc(20vh - 10px);
    @media screen and (max-height: 600px) {
        padding-bottom: 0;
    }
}
</style>

<style lang="less">
.v3-login-body {
    .uc-common-login {
        display: none;
    }

    &.is-login-show {
        overflow: hidden;
        max-height: 100vh;
        .uc-common-login {
            display: block;
            #common-login.uc-common-login-layer {
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                background: linear-gradient(135deg, #DCE9FB 0%, #E8F4F3 100%) !important;
                // #close-common-login-layer {
                //     right: unset;
                //     left: 16px;
                //     top: 16px;

                //     background-image: none !important;
                //     background-color: transparent;
                //     &::before,
                //     &::after {
                //         content: '';
                //         position: absolute;
                //         top: 50%;
                //         left: 50%;
                //         width: 18px;
                //         height: 1px;
                //         background: #000;
                //     }
                //     &::before {
                //         transform: translate(-50%, -50%) rotate(45deg);
                //     }
                //     &::after {
                //         transform: translate(-50%, -50%) rotate(-45deg);
                //     }
                // }
            }
        }

        #uc-common-login-mask {
            background: rgba(0, 0, 0, 0.2);
        }

        .qd-login-v3-root {
            filter: blur(7px);
        }
    }
}
</style>
